import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll:';
const RESTORE_TIMEOUT_MS = 3000;

// The first page of a freshly opened (not reloaded, not "back") visit always gets the same
// history key, 'default'. Forget anything saved under it by an earlier visit in this tab, so
// typing or sharing a link starts at the top. Runs at load, before any page renders.
(() => {
  try {
    const [entry] = performance.getEntriesByType('navigation');
    if (entry?.type === 'navigate' && !window.history.state?.key) {
      sessionStorage.removeItem(`${STORAGE_PREFIX}default`);
      sessionStorage.removeItem('grid:default'); // product count saved by IncrementalGrid
    }
  } catch (e) {
    // Older browsers: nothing to clear
  }
})();

const readPosition = (key) => {
  try {
    const value = sessionStorage.getItem(STORAGE_PREFIX + key);
    return value === null ? null : Number(value);
  } catch (e) {
    return null;
  }
};

const savePosition = (key, y) => {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, String(Math.round(y)));
  } catch (e) {
    // Private mode / storage full: going back just starts at the top
  }
};

// Opening a page starts at the top; going back (or forward) returns to where you were.
// Positions are remembered per history entry for this browser tab.
const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);
  const pathRef = useRef(location.pathname);

  // We restore positions ourselves; the browser's own attempt runs before the page has rendered
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Keep remembering the position of the current page
  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        savePosition(keyRef.current, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  useLayoutEffect(() => {
    const pathChanged = pathRef.current !== location.pathname;
    keyRef.current = location.key;
    pathRef.current = location.pathname;

    if (navigationType !== 'POP') {
      // New page: start at the top. Replacing the URL on the same page (e.g. typing in the
      // search box) keeps the current position.
      if (pathChanged) window.scrollTo(0, 0);
      return undefined;
    }

    const target = readPosition(location.key);
    if (!target) {
      window.scrollTo(0, 0);
      return undefined;
    }

    // Back/forward: the page renders in steps (code, data, more products), so keep trying
    // until it is tall enough, and stop as soon as the visitor scrolls themselves
    let frame = null;
    const startedAt = Date.now();
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
    const attempt = () => {
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, Math.min(target, maxY));
      if (maxY >= target || Date.now() - startedAt > RESTORE_TIMEOUT_MS) {
        stop();
        return;
      }
      frame = requestAnimationFrame(attempt);
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    attempt();
    return stop;
  }, [location.key, location.pathname, navigationType]);

  return null;
};

export default ScrollToTop;
